import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import type { BlogPost } from "@/hooks/useBlogPosts";
import type { ExportContentOptions } from "@/lib/blogExport";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const defaultContentOptions: ExportContentOptions = {
  includeTitle: true,
  includeBody: true,
  includeSocialMediaScript: true,
  includeIntroAndTags: true,
};

interface BlogExportDialogProps {
  open: boolean;
  posts: BlogPost[];
  availableTags: Array<{ id: string; name: string; slug: string }>;
  onOpenChange: (open: boolean) => void;
  onExport: (posts: BlogPost[], options: ExportContentOptions) => void;
}

const BlogExportDialog = ({ open, posts, availableTags, onOpenChange, onExport }: BlogExportDialogProps) => {
  const [selectedStatuses, setSelectedStatuses] = useState({ published: true, unpublished: true });
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [selectedPostIds, setSelectedPostIds] = useState<Set<string>>(new Set());
  const [contentOptions, setContentOptions] = useState<ExportContentOptions>(defaultContentOptions);

  useEffect(() => {
    if (!open) return;
    setSelectedStatuses({ published: true, unpublished: true });
    setSelectedTags(new Set());
    setSelectedPostIds(new Set(posts.map((post) => post.id)));
    setContentOptions(defaultContentOptions);
  }, [open, posts]);

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      if (!selectedStatuses.published && post.published) return false;
      if (!selectedStatuses.unpublished && !post.published) return false;

      if (selectedTags.size > 0) {
        const postTagSlugs = post.post_tags?.map((pt) => pt.tags?.slug).filter(Boolean) ?? [];
        return postTagSlugs.some((slug) => slug && selectedTags.has(slug));
      }

      return true;
    });
  }, [posts, selectedStatuses, selectedTags]);

  const selectedPosts = useMemo(
    () => posts.filter((post) => selectedPostIds.has(post.id)),
    [posts, selectedPostIds],
  );

  const toggleTag = (slug: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  };

  const togglePost = (postId: string) => {
    setSelectedPostIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  const handleSelectAllFiltered = () => {
    setSelectedPostIds((prev) => {
      const next = new Set(prev);
      filteredPosts.forEach((post) => next.add(post.id));
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedPostIds(new Set());
  };

  const handleExport = () => {
    if (selectedPosts.length === 0) {
      toast.error("Select at least one post to export");
      return;
    }

    if (!Object.values(contentOptions).some(Boolean)) {
      toast.error("Select at least one content section to include");
      return;
    }

    onExport(selectedPosts, contentOptions);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Export blog posts</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Which posts</h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="export-status-published"
                  checked={selectedStatuses.published}
                  onCheckedChange={(checked) =>
                    setSelectedStatuses((prev) => ({ ...prev, published: checked === true }))
                  }
                />
                <Label htmlFor="export-status-published">Published</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="export-status-unpublished"
                  checked={selectedStatuses.unpublished}
                  onCheckedChange={(checked) =>
                    setSelectedStatuses((prev) => ({ ...prev, unpublished: checked === true }))
                  }
                />
                <Label htmlFor="export-status-unpublished">Unpublished</Label>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">By tag</h3>
            {availableTags.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tags available yet.</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {availableTags.map((tag) => (
                  <div key={tag.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`export-tag-${tag.slug}`}
                      checked={selectedTags.has(tag.slug)}
                      onCheckedChange={() => toggleTag(tag.slug)}
                    />
                    <Label htmlFor={`export-tag-${tag.slug}`}>{tag.name}</Label>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground">Select specific posts</h3>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleSelectAllFiltered}>
                  Select all shown
                </Button>
                <Button size="sm" variant="ghost" onClick={handleClearSelection}>
                  Clear selection
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Showing {filteredPosts.length} post{filteredPosts.length === 1 ? "" : "s"} based on your filters.
            </p>
            <div className="space-y-2 rounded-md border border-border p-3">
              {filteredPosts.length === 0 ? (
                <p className="text-sm text-muted-foreground">No posts match the selected filters.</p>
              ) : (
                filteredPosts.map((post) => {
                  const tagNames = post.post_tags?.map((pt) => pt.tags?.name).filter(Boolean).join(", ");
                  return (
                    <div key={post.id} className="flex items-start space-x-2">
                      <Checkbox
                        id={`export-post-${post.id}`}
                        checked={selectedPostIds.has(post.id)}
                        onCheckedChange={() => togglePost(post.id)}
                        className="mt-0.5"
                      />
                      <Label htmlFor={`export-post-${post.id}`} className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-foreground">{post.title}</span>
                        <span className="text-xs text-muted-foreground">
                          {post.published ? "Published" : "Unpublished"}
                          {tagNames ? ` • ${tagNames}` : ""}
                        </span>
                      </Label>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Post content to include</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="export-content-title"
                  checked={contentOptions.includeTitle}
                  onCheckedChange={(checked) =>
                    setContentOptions((prev) => ({ ...prev, includeTitle: checked === true }))
                  }
                />
                <Label htmlFor="export-content-title">Title</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="export-content-body"
                  checked={contentOptions.includeBody}
                  onCheckedChange={(checked) =>
                    setContentOptions((prev) => ({ ...prev, includeBody: checked === true }))
                  }
                />
                <Label htmlFor="export-content-body">Body of post</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="export-content-social"
                  checked={contentOptions.includeSocialMediaScript}
                  onCheckedChange={(checked) =>
                    setContentOptions((prev) => ({ ...prev, includeSocialMediaScript: checked === true }))
                  }
                />
                <Label htmlFor="export-content-social">Social media script</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="export-content-intro"
                  checked={contentOptions.includeIntroAndTags}
                  onCheckedChange={(checked) =>
                    setContentOptions((prev) => ({ ...prev, includeIntroAndTags: checked === true }))
                  }
                />
                <Label htmlFor="export-content-intro">Short intro and tags</Label>
              </div>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport}>Export</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlogExportDialog;
